import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';

export type HierarchyMode = 'DEPARTMENT' | 'ZONE';

type HierarchyModeContextType = {
    mode: HierarchyMode;
    setMode: (mode: HierarchyMode) => void;
    canShowHierarchyToggle: boolean;
    updateCanShowHierarchyToggle: (show: boolean) => void;
    companyZoneEnabled: boolean;
    setCompanyZoneEnabled: (enabled: boolean) => void;
};

const HierarchyModeContext = createContext<
    HierarchyModeContextType | undefined
>(undefined);

const STORAGE_KEY = 'hierarchyMode';
const HIERARCHY_TOGGLE_STORAGE_KEY = 'canShowHierarchyToggle';
const DEFAULT_MODE: HierarchyMode = 'DEPARTMENT';

export function HierarchyModeProvider({
    children,
}: Readonly<{
    children: React.ReactNode;
}>) {
    const [mode, setModeState] = useState<HierarchyMode>(DEFAULT_MODE);
    const [canShowHierarchyToggle, setCanShowHierarchyToggle] = useState(() => {
        return localStorage.getItem(HIERARCHY_TOGGLE_STORAGE_KEY) === 'true';
    });
    const [companyZoneEnabled, setCompanyZoneEnabled] = useState(false);

    const setMode = (newMode: HierarchyMode) => {
        setModeState(newMode);
        localStorage.setItem(STORAGE_KEY, newMode);
    };

    const updateCanShowHierarchyToggle = (show: boolean) => {
        setCanShowHierarchyToggle(show);
        localStorage.setItem(HIERARCHY_TOGGLE_STORAGE_KEY, String(show));
    };

    // Multi-tab sync (optional)
    useEffect(() => {
        const sync = (e: StorageEvent) => {
            if (e.key === STORAGE_KEY && e.newValue)
                setModeState(e.newValue as HierarchyMode);
        };
        window.addEventListener('storage', sync);
        return () => window.removeEventListener('storage', sync);
    }, []);

    const values = useMemo(
        () => ({
            mode,
            setMode,
            canShowHierarchyToggle,
            updateCanShowHierarchyToggle,
            companyZoneEnabled,
            setCompanyZoneEnabled,
        }),
        [
            mode,
            setMode,
            canShowHierarchyToggle,
            updateCanShowHierarchyToggle,
            companyZoneEnabled,
            setCompanyZoneEnabled,
        ],
    );

    return (
        <HierarchyModeContext.Provider value={values}>
            {children}
        </HierarchyModeContext.Provider>
    );
}

export function useHierarchyMode() {
    const ctx = useContext(HierarchyModeContext);
    if (!ctx)
        throw new Error(
            'useHierarchyMode must be used within HierarchyModeProvider',
        );
    return ctx;
}
